import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

const VERSION_URL = '/version.json';
/** Schneller pollen — Edge/CDN braucht oft einen zweiten Versuch */
const POLL_MS = 10_000;
/** Nach fehlgeschlagenem Fetch kurz erneut versuchen */
const RETRY_MS = 3_000;

/** In Production vom Build eingebrannt — Baseline = „welche JS-Version läuft gerade“ */
const BUNDLED_VERSION = (process.env.REACT_APP_BUILD_VERSION || '').trim();

/** Entfernt Cache-Bust-Parameter `_=` aus der Adresszeile (ohne Reload). */
function scrubCacheBustQueryParams(): void {
    try {
        const current = new URLSearchParams(window.location.search || '');
        if (!current.has('_')) return;
        const keep = new URLSearchParams();
        current.forEach((value, key) => {
            if (key !== '_') keep.append(key, value);
        });
        const qs = keep.toString();
        const next = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash || ''}`;
        window.history.replaceState({}, '', next);
    } catch {
        /* ignore */
    }
}

type VersionPayload = { version?: string };

async function fetchServerVersion(): Promise<string | null> {
    try {
        const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
            cache: 'no-store',
            headers: { Accept: 'application/json', 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
        });
        if (!res.ok) return null;
        const text = await res.text();
        const trimmed = String(text || '').trim();
        // SPA-Fallback (index.html) oder leerer Body → kein Update-Signal
        if (!trimmed || trimmed.startsWith('<') || trimmed.startsWith('<!')) return null;
        let data: VersionPayload;
        try {
            data = JSON.parse(trimmed) as VersionPayload;
        } catch {
            return null;
        }
        const v = typeof data?.version === 'string' ? data.version.trim() : '';
        return v || null;
    } catch {
        return null;
    }
}

const AutoUpdateModal: React.FC = () => {
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const checkingRef = useRef(false);
    const failCountRef = useRef(0);
    const retryTimerRef = useRef<number | null>(null);

    const checkForUpdate = useCallback(async () => {
        // Dev (npm start): keine Build-ID → kein Modal
        if (!BUNDLED_VERSION || checkingRef.current || updateAvailable) return;
        checkingRef.current = true;
        try {
            const remote = await fetchServerVersion();
            if (!remote) {
                failCountRef.current += 1;
                // Nach Fehlern kurz nachziehen (CDN / kurze Aussetzer)
                if (failCountRef.current <= 5 && retryTimerRef.current == null) {
                    retryTimerRef.current = window.setTimeout(() => {
                        retryTimerRef.current = null;
                        void checkForUpdate();
                    }, RETRY_MS);
                }
                return;
            }
            failCountRef.current = 0;
            if (remote !== BUNDLED_VERSION) {
                setUpdateAvailable(true);
            }
        } finally {
            checkingRef.current = false;
        }
    }, [updateAvailable]);

    useEffect(() => {
        scrubCacheBustQueryParams();
        if (!BUNDLED_VERSION) return;

        void checkForUpdate();
        const intervalId = window.setInterval(() => void checkForUpdate(), POLL_MS);

        const onVisibility = () => {
            if (document.visibilityState === 'visible') void checkForUpdate();
        };
        const onOnline = () => void checkForUpdate();

        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('focus', onOnline);
        window.addEventListener('online', onOnline);
        window.addEventListener('pageshow', onOnline);

        return () => {
            window.clearInterval(intervalId);
            if (retryTimerRef.current != null) {
                window.clearTimeout(retryTimerRef.current);
                retryTimerRef.current = null;
            }
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('focus', onOnline);
            window.removeEventListener('online', onOnline);
            window.removeEventListener('pageshow', onOnline);
        };
    }, [checkForUpdate]);

    const handleReload = () => {
        // Hard-Navigation: genau ein Cache-Bust `_`, keine wachsende `_=`-Kette
        if (window.location.pathname.includes('/onboarding')) {
            window.location.replace(`/?_=${Date.now()}`);
            return;
        }
        const path = window.location.pathname || '/';
        const keep = new URLSearchParams();
        const current = new URLSearchParams(window.location.search || '');
        current.forEach((value, key) => {
            if (key !== '_') keep.append(key, value);
        });
        keep.set('_', String(Date.now()));
        window.location.replace(`${path}?${keep.toString()}`);
    };

    return (
        <AnimatePresence>
            {updateAvailable && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[200000] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
                >
                    <motion.div
                        initial={{ scale: 0.95 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0.95 }}
                        className="bg-[#15171e] border border-[#E6C299]/40 rounded-2xl p-8 max-w-md w-full shadow-[0_0_50px_rgba(230,194,153,0.15)] flex flex-col items-center text-center"
                    >
                        <div className="w-16 h-16 bg-[#E6C299]/10 rounded-full flex items-center justify-center mb-6 shadow-inner">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="32"
                                height="32"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#E6C299"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                                <path d="M3 3v5h5" />
                                <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" />
                                <path d="M16 16h5v5" />
                            </svg>
                        </div>
                        <h2 className="text-2xl font-black text-white uppercase tracking-widest mb-4">
                            Update verfügbar
                        </h2>
                        <p className="text-slate-300 mb-8 text-sm leading-relaxed">
                            Eine neue Version von PreisExpert liegt bereit.
                            <br />
                            <strong className="text-[#E6C299]">Bitte jetzt aktualisieren</strong>, damit
                            alle Funktionen korrekt laufen.
                        </p>
                        <button
                            type="button"
                            onClick={handleReload}
                            className="w-full bg-[#E6C299] hover:bg-white text-black font-black uppercase tracking-widest text-sm py-4 rounded-xl transition-colors shadow-lg"
                        >
                            Jetzt aktualisieren
                        </button>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default AutoUpdateModal;
